import React, {
  useEffect, useMemo, useRef, useState,
} from 'react';
import PropTypes from 'prop-types';
import classes from '../../../pipeline.module.scss';
import Api from '../../../../../../../api/api';
import Table from '../../../../../../_common/Tables/Table';
import { Card } from '../../PipelineComponents';
import SummaryDetailsModal from './SummaryDetailsModal';
import { buildSummaryRows } from './pivotHelpers';
import { formattedCount } from '../../../pipelineHelpers';

const levelColumns = [
  { name: 'LOB', path: 'lob' },
  { name: 'Final Outcome', path: 'finalOutcome' },
  { name: 'Source', path: 'source' },
];

const countColumns = [
  { name: 'Count of Members', path: 'memberCount' },
  { name: 'Count of Status', path: 'statusCount' },
  { name: 'Count of Action Needed', path: 'actionNeededByCount' },
  { name: 'Count of Final Outcome', path: 'finalOutcomeCount' },
  { name: 'Count of Source', path: 'sourceCount' },
];

const labelClass = (row) => [
  classes.summary_label,
  classes[`summary_label_${row.level}`],
  row.level === 0 ? classes.summary_group : '',
].join(' ');

const levelHeader = levelColumns.map((column) => ({
  ...column,
  render: (row) => (
    <span className={labelClass(row)}>
      {row[column.path] || ''}
    </span>
  ),
}));

const countHeader = countColumns.map((column) => ({
  ...column,
  render: (row) => (
    <span className={row.isTotal ? classes.pivot_total_cell : classes.pivot_cell}>
      {row[column.path] ? formattedCount(row[column.path]) : ''}
    </span>
  ),
}));

const buildHeader = (rows) => [
  ...levelHeader,
  ...countHeader.filter((column) => rows.some((row) => row[column.path])),
];

const SummaryCard = ({ params }) => {
  const firstLoad = useRef(true);

  const [summary, setSummary] = useState({});
  const [loading, loadingToggle] = useState(true);

  const [detailsModalData, setDetailsModalData] = useState({ open: false, filters: {} });

  useEffect(() => {
    (async () => {
      try {
        loadingToggle(true);

        const { data } = await Api.getIehpSummary(params);

        setSummary(data?.data || {});

        loadingToggle(false);
        firstLoad.current = false;
      } catch (e) {
        console.warn(e);
      }
    })();
  }, [params]);

  const rows = useMemo(() => buildSummaryRows(summary), [summary]);

  const header = useMemo(() => buildHeader(rows), [rows]);

  const onRowClick = (row) => setDetailsModalData({ open: true, filters: row.filters });

  return (
    <>
      <Card
        title="Summary by LOB, Final Outcome and Source"
        caption="Every level of the pipeline with its counts, down to the source"
        loading={loading}
        firstLoad={firstLoad.current}
        isEmpty={!rows.length}
      >
        <div className={classes.pipeline_table}>
          <Table
            header={header}
            data={rows}
            className={classes.clickable_row}
            onClick={onRowClick}
            getFullRowData
          />
        </div>
      </Card>

      <SummaryDetailsModal
        isOpen={detailsModalData.open}
        params={params}
        filters={detailsModalData.filters}
        onClose={() => setDetailsModalData((prev) => ({ ...prev, open: false }))}
      />
    </>
  );
};

SummaryCard.propTypes = {
  params: PropTypes.object.isRequired,
};

export default SummaryCard;
